/**
 * Does the current location already sit on `targetUrl`'s route?
 *
 * The booking guards (EventPage, ReserveCollection, the guest-cancellation /
 * -reschedule flow, and the booking-session hooks) redirect to a hand-built
 * canonical URL (`cleanedUrl` / `nextUrl`) whenever the browser isn't on it. The
 * naive check — exact string equality of `pathname + search` against the canonical
 * — forces the guard to first strip every param the router re-adds to the window
 * URL (locale, attribution, …); miss one and the comparison can never match, so the
 * guard `<Redirect>`s forever and the page renders blank.
 *
 * This inverts that fragile denylist into an allowlist: the match cares ONLY about
 * what defines the route — the pathname, and the exact params the canonical URL
 * itself carries. Any other param on the current URL (sticky locale, attribution, an
 * unknown host param, a tracking key we haven't enumerated yet) is irrelevant to "am
 * I on the right page?" and is ignored, so a stray param can never trigger the
 * redirect loop. Mode-agnostic: in hash/browser mode the current search carries only
 * route params, so it behaves exactly like the old equality; in query mode it
 * tolerates the sticky/external params the codec re-adds.
 */
export const isAtRoute = (
  location: { pathname: string; search: string },
  targetUrl: string,
): boolean => {
  const [pathname, query = ''] = targetUrl.split('?');
  if (location.pathname !== pathname) {
    return false;
  }

  const current = new URLSearchParams(location.search);
  let matchesRequired = true;
  new URLSearchParams(query).forEach((value, key) => {
    if (current.get(key) !== value) {
      matchesRequired = false;
    }
  });
  return matchesRequired;
};
